import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import type InternalInterviewListEntryView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewListEntryView';
import ImportedDocumentTitle from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection/ElementRow/ImportedDocumentTitle';
import InterviewTitle from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/InterviewTitle';
import ElementLine from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection/ElementLine';
import TranscriptionIssue from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/TranscriptionIssue';
import type { ElementTileKind } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/ElementTile';
import LaunchTranscriptionButton from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection/ElementRow/LaunchTranscriptionButton';
import LocalRecordingActions from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection/LocalRecordingActions';
import RemoveElementAction from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection/ElementRow/RemoveElementAction';
import resolveElementStateSeverity from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection/ElementRow/Helpers/resolveElementStateSeverity';
import useSubjectInterviews from '@UI/Pages/Beneficiaries/Hooks/useSubjectInterviews';
import useSubjectImportedDocuments from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Hooks/useSubjectImportedDocuments';
import useElementDescriber from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Hooks/useElementDescriber';
import { openAddFromDossierSheet } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Signals/addFromDossierSheetSignal';
import type WorkflowRunSourceView from '@UI/Store/WorkflowRuns/Views/WorkflowRunSourceView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Tag } from '@verclam/ui-kit';

const linkStyle = css`
	align-self: flex-start;
	padding: 0;
	background: transparent;
	border: none;
	cursor: pointer;
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.3;
	color: ${theme.colors.accent};

	&:hover,
	&:focus-visible {
		color: ${theme.colors.accentDark};
	}
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
	readonly element: WorkflowRunSourceView;
	/** Whether « Retirer » (and « Choisir un autre … » on a carried-over document) is offered. */
	readonly canEdit: boolean;
	/** Whether « Lancer la transcription » is offered (permission, dossier not archived). */
	readonly canWrite: boolean;
	/** The device-side entry of an element still being sent, which carries the retry actions. */
	readonly localEntry: InternalInterviewListEntryView | null;
};

/**
 * One element of the preparation — an interview, a dictated note, a document or an imported document — with its server state,
 * a link to the existing detail screen (transcript, consent) and its actions. A document carried over
 * automatically (`declaredDocumentTypeId`) offers « Choisir un autre … » instead of « Retirer ».
 */
const ElementRow: React.FC<Props> = ({ subject, preparationId, element, canEdit, canWrite, localEntry }) => {
	const { describeElement, labelOfDocumentType } = useElementDescriber(subject);
	const description = describeElement(element);
	const carriedOverTypeId = element.declaredDocumentTypeId;
	const carriedOverLabel = carriedOverTypeId !== null ? labelOfDocumentType(carriedOverTypeId) : '';

	const { data: interviews } = useSubjectInterviews(subject);
	const { data: importedDocuments } = useSubjectImportedDocuments(subject);

	// The pencil only shows for an object held by the store: a rename of a fallback title could not show up.
	const interviewId = element.interviewId;
	const importedDocumentId = element.importedDocumentId;
	let title: React.ReactNode = description.title;
	if (interviewId !== null && interviews !== null && interviews.findById(interviewId) !== null) {
		title = (
			<InterviewTitle
				subject={subject}
				interviewId={interviewId}
				title={description.title}
				canWriteDossier={canWrite}
			/>
		);
	} else if (
		importedDocumentId !== null &&
		carriedOverTypeId === null &&
		importedDocuments !== null &&
		importedDocuments.findById(importedDocumentId) !== null
	) {
		title = (
			<ImportedDocumentTitle
				subject={subject}
				importedDocumentId={importedDocumentId}
				title={description.title}
				canRename={canWrite}
			/>
		);
	}

	let kind: ElementTileKind = 'document';
	if (carriedOverTypeId !== null) {
		kind = 'automatic';
	} else if (element.interviewId !== null) {
		kind = element.category.value === 'notes' ? 'notes' : 'interview';
	} else if (element.importedDocumentId !== null) {
		kind = 'imported';
	}

	const detailLink = React.useMemo(() => {
		if (element.interviewId !== null) {
			return {
				to: `${buildSubjectPath(subject)}/interviews/${element.interviewId}`,
				label: element.category.value === 'notes' ? 'Voir la note' : "Voir l'entretien",
			};
		}

		if (element.documentId !== null) {
			return {
				to: `${buildSubjectPath(subject)}/documents/${element.documentId}`,
				label: 'Voir le document',
			};
		}

		return null;
	}, [subject, element.interviewId, element.documentId, element.category.value]);

	const handleChooseAnotherClick = React.useCallback(() => {
		if (carriedOverTypeId !== null) {
			openAddFromDossierSheet({ replacesDocumentTypeId: carriedOverTypeId });
		}
	}, [carriedOverTypeId]);

	const hasFooter = element.transcriptionIssue !== null || (localEntry !== null && canEdit);

	const links = (
		<React.Fragment>
			<ConditionalLoader data={detailLink}>
				{(detail) => (
					<Link css={linkStyle} to={detail.to}>
						{detail.label}
					</Link>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={carriedOverTypeId !== null && canEdit}>
				<button type="button" css={linkStyle} onClick={handleChooseAnotherClick}>
					Choisir un autre {carriedOverLabel}
				</button>
			</ConditionalLoader>
		</React.Fragment>
	);

	return (
		<ElementLine
			kind={kind}
			title={title}
			subtitle={
				carriedOverTypeId !== null
					? `Reprend automatiquement le dernier ${carriedOverLabel}`
					: description.subtitle
			}
			links={links}
			stateReason={element.stateReason}
			chip={
				<Tag
					rounded
					severity={resolveElementStateSeverity(element.state.value)}
					value={element.state.label}
				/>
			}
			action={
				<React.Fragment>
					<ConditionalLoader data={canWrite && element.canTranscribe}>
						<LaunchTranscriptionButton
							subject={subject}
							preparationId={preparationId}
							elementId={element.id}
						/>
					</ConditionalLoader>
					<ConditionalLoader data={canEdit && carriedOverTypeId === null}>
						<RemoveElementAction
							subject={subject}
							preparationId={preparationId}
							elementId={element.id}
							elementTitle={description.title}
						/>
					</ConditionalLoader>
				</React.Fragment>
			}
			footer={
				hasFooter ? (
					<React.Fragment>
						<ConditionalLoader data={element.transcriptionIssue}>
							{(issue) => <TranscriptionIssue subject={subject} issue={issue} />}
						</ConditionalLoader>
						<ConditionalLoader data={localEntry !== null && canEdit ? localEntry : null}>
							{(entry) => <LocalRecordingActions entry={entry} subject={subject} />}
						</ConditionalLoader>
					</React.Fragment>
				) : undefined
			}
		/>
	);
};

export default ElementRow;
